import { useEffect, useCallback } from "react";
import "../blocks/galleryLightbox/galleryLightbox.css";
import { getYoutubeThumbnail } from "../utils/youtube";
import { getEmbeddableVideo } from "../utils/videoEmbed";

const GalleryLightbox = ({ items, activeIndex, onClose, onNavigate, onDelete }) => {
  const item = items[activeIndex];

  const handlePrev = useCallback(() => {
    onNavigate((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, items.length, onNavigate]);

  const handleNext = useCallback(() => {
    onNavigate((activeIndex + 1) % items.length);
  }, [activeIndex, items.length, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handlePrev, handleNext, onClose]);

  if (!item) return null;

  const isLinkVideo = item.kind === "video" && item.source === "link";
  const embeddable = isLinkVideo ? getEmbeddableVideo(item.url) : null;
  const linkThumbnail = isLinkVideo && !embeddable ? getYoutubeThumbnail(item.url) : null;

  return (
    <div className="galleryLightbox" onClick={onClose}>
      <button
        type="button"
        className="galleryLightbox__close"
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>

      {items.length > 1 && (
        <button
          type="button"
          className="galleryLightbox__nav galleryLightbox__nav_prev"
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}

      <div className="galleryLightbox__content" onClick={(e) => e.stopPropagation()}>
        {item.kind === "video" && item.source === "upload" && (
          <video className="galleryLightbox__media" src={item.url} controls autoPlay />
        )}

        {isLinkVideo && embeddable?.kind === "direct" && (
          <video
            className="galleryLightbox__media"
            src={embeddable.url}
            controls
            autoPlay
          />
        )}

        {isLinkVideo && (embeddable?.kind === "youtube" || embeddable?.kind === "vimeo") && (
          <iframe
            className="galleryLightbox__iframe"
            src={embeddable.embedUrl}
            title={item.title || "Embedded video"}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        )}

        {isLinkVideo && !embeddable && (
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            {linkThumbnail ? (
              <img
                className="galleryLightbox__media"
                src={linkThumbnail}
                alt={item.title || "Video link"}
              />
            ) : (
              <div className="galleryLightbox__link-placeholder">
                Can't play this one here — open the original link to watch.
              </div>
            )}
          </a>
        )}

        {item.kind === "photo" && (
          <img className="galleryLightbox__media" src={item.url} alt={item.title || "Album media"} />
        )}

        <div className="galleryLightbox__footer">
          {item.title && <span className="galleryLightbox__title">{item.title}</span>}
          {item.source === "link" && (
            <a
              className="galleryLightbox__open-link"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open original ↗
            </a>
          )}
          <button
            type="button"
            className="galleryLightbox__delete"
            onClick={() => onDelete(item)}
          >
            Delete
          </button>
        </div>
      </div>

      {items.length > 1 && (
        <button
          type="button"
          className="galleryLightbox__nav galleryLightbox__nav_next"
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>
  );
};

export default GalleryLightbox;
